import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Eye,
  EyeOff,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  ShoppingBag,
  Package,
  TrendingUp,
  Info,
  Tag,
  Truck,
  Key,
  Lock,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuth } from '@/context/AuthContext';
import { useSubscription } from '@/context/SubscriptionContext';

export interface N11Credentials {
  appKey: string;
  appSecret: string;
}

interface N11ConnectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (credentials: N11Credentials, connectionId: string) => void;
}

export function N11ConnectModal({
  isOpen,
  onClose,
  onSuccess,
}: N11ConnectModalProps) {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  const { user } = useAuth();
  const { checkDuplicateApiKey } = useSubscription();

  const [appKey, setAppKey] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'form' | 'validating' | 'success'>('form');
  const [storeName, setStoreName] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!appKey.trim()) {
      setError(isEnglish ? 'API Key is required' : 'API Anahtari zorunludur');
      return;
    }

    if (!appSecret.trim()) {
      setError(isEnglish ? 'API Secret is required' : 'API Sifresi zorunludur');
      return;
    }

    if (!user?.uid) {
      setError(isEnglish ? 'Please login first' : 'Lutfen once giris yapin');
      return;
    }

    setIsValidating(true);
    setStep('validating');

    try {
      // Check for duplicate API key (mukerrer hesap kontrolu)
      const duplicateCheck = await checkDuplicateApiKey(appKey.trim(), 'n11');
      if (duplicateCheck.isDuplicate) {
        setStep('form');
        setError(duplicateCheck.message || (isEnglish
          ? 'Duplicate account detected! This API Key is already used in another account. Please check your main account or contact support.'
          : 'Mukerrer hesap tespit edildi! Bu API Anahtari baska bir hesapta kullanilmaktadir. Lutfen ana hesabinizi kontrol edin veya destek ile iletisime gecin.'));
        setIsValidating(false);
        return;
      }

      const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';

      // Save connection via Cloud Function (validates and saves to Firestore)
      const response = await fetch(`${FUNCTIONS_URL}/saveN11`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.uid,
          appKey: appKey.trim(),
          appSecret: appSecret.trim(),
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        setStep('form');
        setError(result.error || (isEnglish ? 'Invalid credentials' : 'Gecersiz kimlik bilgileri'));
        setIsValidating(false);
        return;
      }

      // Success!
      const credentials: N11Credentials = {
        appKey: appKey.trim(),
        appSecret: appSecret.trim(),
      };

      setStoreName(result.storeName || '');
      setStep('success');
      setTimeout(() => {
        onSuccess(credentials, result.connectionId);
        handleClose();
      }, 1500);
    } catch (err) {
      setStep('form');
      setError(
        err instanceof Error
          ? err.message
          : isEnglish
          ? 'Connection failed. Please try again.'
          : 'Baglanti basarisiz. Lutfen tekrar deneyin.'
      );
    } finally {
      setIsValidating(false);
    }
  };

  const handleClose = () => {
    setAppKey('');
    setAppSecret('');
    setShowSecret(false);
    setError(null);
    setStep('form');
    setStoreName('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-bg-surface rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-border-default">
        {/* Header with N11 Branding */}
        <div
          className="relative p-6 pb-8"
          style={{
            background: 'linear-gradient(135deg, #00B14F 0%, #00963F 100%)',
          }}
        >
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
          >
            <X size={18} />
          </button>

          <div className="flex items-center gap-4">
            {/* N11 Logo */}
            <div className="w-14 h-14 rounded-xl bg-white flex items-center justify-center shadow-lg">
              <svg viewBox="0 0 32 32" className="w-10 h-10">
                <rect fill="#00B14F" width="32" height="32" rx="6" />
                <text
                  x="16"
                  y="21"
                  textAnchor="middle"
                  fill="white"
                  fontSize="12"
                  fontWeight="bold"
                  fontFamily="Arial"
                >
                  n11
                </text>
              </svg>
            </div>
            <div className="text-white">
              <h2 className="text-xl font-bold">N11</h2>
              <p className="text-white/80 text-sm">
                {isEnglish ? 'Connect your seller account' : 'Satici hesabinizi baglayin'}
              </p>
            </div>
          </div>

          {/* Features */}
          <div className="flex flex-wrap gap-3 mt-6">
            {[
              { icon: ShoppingBag, label: isEnglish ? 'Orders' : 'Siparisler' },
              { icon: Package, label: isEnglish ? 'Products' : 'Urunler' },
              { icon: Tag, label: isEnglish ? 'Categories' : 'Kategoriler' },
              { icon: Truck, label: isEnglish ? 'Shipping' : 'Kargo' },
              { icon: TrendingUp, label: isEnglish ? 'Analytics' : 'Analitik' },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2 text-white/90 text-sm">
                <Icon size={14} />
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 'validating' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-[#00B14F]/10 flex items-center justify-center mx-auto mb-4">
                <Loader2 size={32} className="text-[#00B14F] animate-spin" />
              </div>
              <h3 className="text-lg font-semibold text-text-primary mb-2">
                {isEnglish ? 'Validating credentials...' : 'Kimlik bilgileri dogrulaniyor...'}
              </h3>
              <p className="text-sm text-text-secondary">
                {isEnglish
                  ? 'Connecting to N11 API'
                  : "N11 API'sine baglaniliyor"}
              </p>
            </div>
          )}

          {step === 'success' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 size={32} className="text-green-500" />
              </div>
              <h3 className="text-lg font-semibold text-text-primary mb-2">
                {isEnglish ? 'Successfully connected!' : 'Basariyla baglandi!'}
              </h3>
              <p className="text-sm text-text-secondary">
                {storeName
                  ? (isEnglish ? `Connected as ${storeName}` : `${storeName} olarak baglandi`)
                  : (isEnglish ? 'Your N11 account is now connected' : 'N11 hesabiniz artik bagli')}
              </p>
            </div>
          )}

          {step === 'form' && (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Error Banner */}
              {error && (
                <div className="flex items-start gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/30">
                  <AlertCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              {/* Info Banner */}
              <div className="flex items-start gap-3 p-4 rounded-xl bg-[#00B14F]/10 border border-[#00B14F]/30">
                <Info size={20} className="text-[#00B14F] flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="text-[#00963F] font-medium mb-1">
                    {isEnglish ? 'Where to find your API credentials?' : 'API bilgilerinizi nereden bulabilirsiniz?'}
                  </p>
                  <p className="text-text-secondary">
                    {isEnglish
                      ? 'Go to N11 Seller Panel → My Account → API Informations'
                      : 'N11 Satici Paneli → Hesabim → API Bilgileri'}
                  </p>
                </div>
              </div>

              {/* API Key */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  API {isEnglish ? 'Key' : 'Anahtari'}
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-text-tertiary">
                    <Key size={18} />
                  </div>
                  <input
                    type="text"
                    value={appKey}
                    onChange={(e) => setAppKey(e.target.value)}
                    placeholder="5d0aacd0-7832-44a8-bdeb-d5c5ff59da35"
                    className="w-full px-4 py-3 pl-11 rounded-xl bg-bg-elevated border border-border-default focus:border-[#00B14F] focus:outline-none focus:ring-2 focus:ring-[#00B14F]/20 text-text-primary placeholder:text-text-tertiary transition-all font-mono text-sm"
                  />
                </div>
              </div>

              {/* API Secret */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  API {isEnglish ? 'Secret' : 'Sifresi'}
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-text-tertiary">
                    <Lock size={18} />
                  </div>
                  <input
                    type={showSecret ? 'text' : 'password'}
                    value={appSecret}
                    onChange={(e) => setAppSecret(e.target.value)}
                    placeholder="IHDELyYMkBK5s9M4"
                    className="w-full px-4 py-3 pl-11 pr-12 rounded-xl bg-bg-elevated border border-border-default focus:border-[#00B14F] focus:outline-none focus:ring-2 focus:ring-[#00B14F]/20 text-text-primary placeholder:text-text-tertiary transition-all font-mono text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecret(!showSecret)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-primary transition-colors"
                  >
                    {showSecret ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-4 py-3 rounded-xl bg-bg-elevated border border-border-default text-text-secondary hover:bg-bg-surface hover:text-text-primary transition-all font-medium"
                >
                  {isEnglish ? 'Cancel' : 'Iptal'}
                </button>
                <button
                  type="submit"
                  disabled={isValidating}
                  className={cn(
                    'flex-1 px-4 py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2',
                    'bg-[#00B14F] text-white hover:bg-[#00963F]',
                    'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                >
                  {isValidating ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      {isEnglish ? 'Connecting...' : 'Baglaniyor...'}
                    </>
                  ) : (
                    isEnglish ? 'Connect' : 'Baglan'
                  )}
                </button>
              </div>

              {/* Help Link */}
              <div className="text-center pt-2">
                <a
                  href="https://magazadestek.n11.com/satis-surecleri?id=27"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-text-tertiary hover:text-[#00B14F] transition-colors"
                >
                  <ExternalLink size={14} />
                  {isEnglish ? 'API Documentation' : 'API Dokumantasyonu'}
                </a>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
